import { mdiArrowLeft, mdiChevronRight, mdiDelete } from '@mdi/js';
import Icon from '@mdi/react';
import useOnlineStatus from '@rehooks/online-status';
import PrimaryButton from 'Components/Buttons/PrimaryButton';
import SecondaryButton from 'Components/Buttons/SecondaryButton';
import FieldTransition from 'Components/FieldTransition';
import FieldWrapper from 'Components/Forms/FieldWrapper';
import FormWrapper from 'Components/Forms/FormWrapper';
import SelectField from 'Components/Forms/SelectField';
import TextareaField from 'Components/Forms/TextareaField';
import TextField from 'Components/Forms/TextField';
import SyncSpinner from 'Components/SyncSpinner';
import { Field, Form, Formik } from 'formik';
import { entityTypeOptions } from 'Pages/Tasks/AddTask';
import { taskStatusOptions } from 'Pages/Tasks/EditTask.jsx';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { Link } from 'react-router-dom';
import api from 'Services/api';
import valueStore from 'Services/valueStore';
import useFormikSubmit from 'Support/hooks/useFormikSubmit';
import usePageTitle from 'Support/hooks/usePageTitle';
import route from 'Support/route';
import * as Yup from 'yup';

const jobClass = 'App\\Models\\Job';
const reportClass = 'App\\Models\\Report';

const EditRecurringTask = () => {
  const { id } = useParams();
  const history = useHistory();

  const isOnline = useOnlineStatus();
  const [currentRecurringTask, setCurrentRecurringTask] = useState([]);
  const [currentTask, setCurrentTask] = useState();
  const [entityOptions, setEntityOptions] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [reports, setReports] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [formikProps, setFormikProps] = useState();
  const [validationSchema, setValidationSchema] = useState({});
  const [loading, setLoading] = useState(true);
  const [deleteWarning, setDeleteWarning] = useState(false);

  const sync = async () => {
    if (!syncing) {
      setSyncing(true);

      setJobs(await api.syncJobs());
      setUsers(await api.syncUsers());
      setReports(await api.syncReports());

      setSyncing(false);
    }
  };

  const deleteRecurringTask = () => {
    if (deleteWarning) {
      setDeleteWarning(false);
      onSubmit({ deleted: true }, {});
    } else setDeleteWarning(true);
  };

  useEffect(async () => {
    setJobs((await valueStore.getArray(`jobs`)) || []);
    setTasks((await valueStore.getArray(`tasks`)) || []);
    setUsers((await valueStore.getArray(`users`)) || []);
    setReports((await valueStore.getArray(`reports`)) || []);
  }, []);

  usePageTitle(`Edit Recurring Task`);

  const onSubmit = useFormikSubmit(async (values) => {
    let newRecurringTask = {
      ...currentTask,
      recurring_task: {
        ...values,
        updated: true,
      },
    };

    if (values.deleted === true) {
      newRecurringTask = {
        ...currentTask,
        recurring_task: {
          id: currentRecurringTask.id,
          deleted: true,
        },
      };
    }
    newRecurringTask['status'] = newRecurringTask['status'].value;
    let tasksForUpload = tasks.map((task) => {
      if (parseInt(id) === task.id) {
        return newRecurringTask;
      }
      return task;
    });

    setTasks(tasksForUpload);
    await valueStore.set(`tasks`, tasksForUpload);

    if (isOnline) {
      await api.syncTasks();
    }
    history.push(route('tasks.edit', { id: id }));
  });

  useEffect(async () => {
    let tempCurrentTask = tasks?.find((task) => task.id === parseInt(id));
    if (tempCurrentTask) {
      setCurrentTask(tempCurrentTask);
      setCurrentRecurringTask(tasks?.find((task) => task.id === parseInt(id)).recurring_task);
    }
  }, [JSON.stringify(tasks), JSON.stringify(currentRecurringTask)]);

  useEffect(async () => {
    if (isOnline) {
      await sync();
    }
  }, [JSON.stringify(tasks)]);

  useEffect(() => {
    if (currentRecurringTask?.id && !formikProps?.initialValues) {
      setFormikProps({
        initialValues: {
          assignee_id: currentRecurringTask.assignee_id || null,
          name: currentRecurringTask.name || '',
          description: currentRecurringTask.description || '',
          entity_type: currentRecurringTask.entity_type || null,
          entity_id: currentRecurringTask.entity_id || null,
          status: currentRecurringTask.status?.value || '',
          recurrence_trigger: currentRecurringTask.recurrence_trigger?.value || currentRecurringTask.recurrence_trigger || 'schedule',
          recurrence_interval_days: currentRecurringTask.recurrence_interval_days || '',
          schedule_anchor: 'today',
          due_at_offset_days: currentRecurringTask.due_at_offset_days || '',
        },
      });
      setValidationSchema(
        Yup.object().shape({
          name: Yup.string().required('Please enter a name'),
          description: Yup.string().required('Please enter a description'),
          recurrence_trigger: Yup.string().oneOf(['schedule', 'on_close']).required('Please choose a trigger'),
          recurrence_interval_days: Yup.mixed().when('recurrence_trigger', {
            is: 'schedule',
            then: Yup.number()
              .transform((value, originalValue) => (originalValue === '' ? null : value))
              .integer('Days must be a whole number')
              .moreThan(0, 'Please set a valid number of days')
              .required('Please set the interval'),
          }),
          due_at_offset_days: Yup.number()
            .transform((value, originalValue) => (originalValue === '' ? null : value))
            .integer('Days must be a whole number')
            .moreThan(0, 'Please set a valid number of days')
            .nullable(),
        }),
      );
      setLoading(false);
    }
  }, [JSON.stringify(currentRecurringTask)]);

  useEffect(() => {
    if (currentRecurringTask?.entity_type === jobClass) {
      setEntityOptions(jobs);
    }
    if (currentRecurringTask?.entity_type === reportClass) {
      setEntityOptions(reports);
    }
  }, [jobs, reports]);

  return (
    <div className="m-4">
      <SyncSpinner loading={loading} />

      <SecondaryButton as={Link} to={route('tasks.edit', { id: id })} className="mb-12 w-full py-2">
        <Icon path={mdiArrowLeft} size={1} className="mr-1" /> Back
      </SecondaryButton>

      {!loading && (
        <Formik {...formikProps} onSubmit={onSubmit} validationSchema={validationSchema}>
          {({ values: { entity_type, recurrence_trigger }, errors }) => {
            useEffect(() => {
              if (entity_type === entityTypeOptions[1].value) {
                setEntityOptions(jobs);
              }
              if (entity_type === entityTypeOptions[2].value) {
                setEntityOptions(reports);
              }
            }, [entity_type]);

            return (
              <Form>
                <FormWrapper className="rounded-lg bg-white shadow-md">
                  {users.length > 0 && (
                    <FieldWrapper>
                      <SelectField name="assignee_id" label="Assignee" options={users} valueKey="id" labelKey="name" />
                    </FieldWrapper>
                  )}

                  <FieldWrapper>
                    <SelectField name="entity_type" label="Link to" options={entityTypeOptions} />
                    <FieldTransition show={entityOptions.length}>
                      <div className="mt-4">
                        <SelectField name="entity_id" label="Job" options={entityOptions} valueKey="id" labelKey="name" />
                      </div>
                    </FieldTransition>
                  </FieldWrapper>

                  <FieldWrapper>
                    <TextField name="name" isRequired={true} />
                  </FieldWrapper>

                  <FieldWrapper>
                    <TextareaField name="description" isRequired={true} className="min-h-42" />
                  </FieldWrapper>

                  <FieldWrapper>
                    <SelectField name="status" options={taskStatusOptions} />
                  </FieldWrapper>

                  <FieldWrapper>
                    <p className="mb-2 text-sm text-gray-800">Create the next task</p>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <Field type="radio" name="recurrence_trigger" value="schedule" className="form-radio" />
                        <span className="ml-2">On a schedule</span>
                      </label>
                      <label className="flex items-center">
                        <Field type="radio" name="recurrence_trigger" value="on_close" className="form-radio" />
                        <span className="ml-2">When this task is closed</span>
                      </label>
                    </div>
                  </FieldWrapper>

                  {recurrence_trigger === 'schedule' && (
                    <FieldWrapper>
                      <p className="flex items-center text-sm text-gray-800">
                        <span className="mr-3 w-10">Every</span>
                        <span className="w-24">
                          <TextField name="recurrence_interval_days" type="number" label={false} />
                        </span>
                        <span className="ml-3">
                          days <span className="text-red-500">*</span>
                        </span>
                      </p>
                    </FieldWrapper>
                  )}

                  <FieldWrapper>
                    <p className="flex items-center text-sm text-gray-800">
                      <span className="mr-3 w-10">Due</span>
                      <span className="w-24">
                        <TextField name="due_at_offset_days" type="number" label={false} />
                      </span>
                      <span className="ml-3">days after creation</span>
                    </p>
                  </FieldWrapper>

                  {deleteWarning && (
                    <p className="pt-6 text-red-500">
                      Are you sure you want to <b>DELETE</b> this Recurring task? Press delete again to accept
                    </p>
                  )}
                  <FieldWrapper className="mt-6 grid grid-cols-2 gap-4">
                    <SecondaryButton type="button" className="bg-red-500" onClick={deleteRecurringTask}>
                      <Icon path={mdiDelete} size={1} />
                    </SecondaryButton>
                    <PrimaryButton type="submit" className="w-full" disabled={Object.values(errors).length}>
                      Submit <Icon path={mdiChevronRight} size={1} />
                    </PrimaryButton>
                  </FieldWrapper>
                </FormWrapper>
              </Form>
            );
          }}
        </Formik>
      )}
    </div>
  );
};

export default EditRecurringTask;
